import axios from "axios";
import { toast } from "sonner";
import { compressImageFile } from "./imageCompression";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
export const backendAssetUrl = (value) =>
    BACKEND_URL && typeof value === "string" && value.startsWith("/")
        ? `${BACKEND_URL}${value}`
        : value;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

/**
 * Достать список из ответа, который может быть как массивом, так и страницей.
 *
 * Списочные ручки отдают обычный массив, пока клиент не попросил страницу
 * параметром `limit` — тогда приходит {items, total, limit, offset}. Такой
 * компромисс позволяет переводить тяжёлые таблицы на пагинацию по одной, не
 * ломая разом все экраны. Здесь — единственное место, где эта развилка
 * разбирается.
 */
export function asRows(data) {
    if (Array.isArray(data)) return data;
    return data?.items || [];
}

/** Общее число записей: для массива — его длина, для страницы — поле total. */
export function asTotal(data) {
    if (Array.isArray(data)) return data.length;
    return data?.total ?? 0;
}

/**
 * Normalise any FastAPI/axios error into a plain string.
 * FastAPI validation errors return detail as an array of objects like
 * [{type, loc, msg, input, ctx}, ...] — we join them into a readable message.
 */
export function extractApiError(err, fallback = "Произошла ошибка") {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((d) => {
                if (typeof d === "string") return d;
                const loc = Array.isArray(d.loc)
                    ? d.loc.filter((x) => x !== "body").join(" → ")
                    : "";
                const msg = d.msg || d.message || JSON.stringify(d);
                return loc ? `${loc}: ${msg}` : msg;
            })
            .join("; ");
    }
    if (typeof detail === "object") return JSON.stringify(detail);
    return String(detail);
}

/**
 * Событие «аккаунт заблокирован за долг».
 *
 * Через window, а не через React-контекст: 403 прилетает из интерцептора,
 * который живёт вне дерева компонентов, а поймать его надо из любой точки
 * приложения — хоть из фонового запроса на странице склада.
 */
export const DEBTOR_BLOCK_EVENT = "app:debtor-block";

export function notifyDebtorBlock(message = "") {
    window.dispatchEvent(new CustomEvent(DEBTOR_BLOCK_EVENT, { detail: { message } }));
}

api.interceptors.request.use(async (config) => {
    const runtime = typeof globalThis === "undefined" ? null : globalThis;
    const FormDataCtor = runtime?.FormData;
    const FileCtor = runtime?.File;

    if (FormDataCtor && config.data instanceof FormDataCtor) {
        const compressedFormData = new FormDataCtor();

        // Rebuild the payload in its original order. Non-image fields and files
        // are appended unchanged; only browser image Files are re-encoded.
        for (const [field, value] of config.data.entries()) {
            const isImageFile =
                FileCtor &&
                value instanceof FileCtor &&
                value.type?.toLowerCase().startsWith("image/");
            const nextValue = isImageFile
                ? await compressImageFile(value)
                : value;

            if (FileCtor && nextValue instanceof FileCtor) {
                compressedFormData.append(field, nextValue, nextValue.name);
            } else {
                compressedFormData.append(field, nextValue);
            }
        }

        config.data = compressedFormData;
    }

    const token = runtime?.localStorage?.getItem("tabel_token");
    if (token) config.headers.Authorization = 'Bearer ' + token;
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (error) => {
        if (error?.response?.status === 401) {
            let target = "/login";
            try {
                const raw = localStorage.getItem("tabel_user");
                if (raw) {
                    const u = JSON.parse(raw);
                    if (u?.modules?.hostel && !u?.modules?.tabel) {
                        target = "/hostel/login";
                    }
                }
            } catch {
                /* ignore */
            }
            localStorage.removeItem("tabel_token");
            localStorage.removeItem("tabel_user");
            localStorage.removeItem("tabel_limits");
            if (window.location.pathname !== target) {
                window.location.href = target;
            }
        }

        // Аккаунт закрыт админом за долг. Сервер отбивает любую запись, но
        // человеку про это должен сказать не тост, а красный экран с текстом
        // администратора, поэтому просто поднимаем флаг — его слушает
        // DebtorBlockGate. Это страховка на случай, когда состояние на
        // клиенте устарело (блокировку включили посреди сессии).
        if (error?.response?.status === 403 && error.response.data?.debtor_block) {
            notifyDebtorBlock(error.response.data?.detail || "");
        }

        // 402 приходит, когда подписка на модуль кончилась и запись закрыта.
        // Не редиректим насильно: человек мог печатать форму десять минут, и
        // унести его со страницы значит стереть введённое. Показываем, что
        // произошло, и даём уйти на оплату самому.
        if (error?.response?.status === 402) {
            const detail = error.response.data?.detail || "Подписка на модуль истекла";
            toast.error(detail, {
                duration: 8000,
                action: {
                    label: "К подписке",
                    onClick: () => { window.location.href = "/subscription"; },
                },
            });
        }

        return Promise.reject(error);
    },
);

export const authApi = {
    login: (email, password) => api.post("/auth/login", { email, password }),
    // accept_terms обязателен: сервер отказывает в регистрации без принятия
    // оферты и политики обработки персональных данных.
    register: (email, password, name, acceptTerms = false) =>
        api.post("/auth/register", { email, password, name, accept_terms: acceptTerms }),
    hostelRegister: (email, password, name, acceptTerms = false) =>
        api.post("/auth/hostel/register", { email, password, name, accept_terms: acceptTerms }),
    me: () => api.get("/auth/me"),
    limits: () => api.get("/me/limits"),
    plans: () => api.get("/plans"),
    settings: () => api.get("/settings"),
    updateSettings: (data) => api.put("/settings", data),
    changePassword: (data) => api.post("/auth/change-password", data),

    // Регистрация и вход теперь могут вернуть 200 БЕЗ токена — с полем
    // verification_required. Это не ошибка, а развилка сценария: показываем
    // экран ввода кода из письма.
    verifyEmail: (email, code) => api.post("/auth/verify-email", { email, code }),
    resendCode: (email) => api.post("/auth/resend-code", { email }),
    requestPasswordReset: (email) => api.post("/auth/password-reset/request", { email }),
    confirmPasswordReset: (email, code, newPassword) =>
        api.post("/auth/password-reset/confirm", { email, code, new_password: newPassword }),
};

// ---- Подписки по модулям ----
// Единого тарифа нет: каждый модуль оплачивается отдельно, со своим сроком.
export const billingApi = {
    overview: () => api.get("/billing/overview"),
    prices: () => api.get("/billing/prices"),
    setPrices: (prices) => api.put("/billing/prices", { prices }),
    createInvoice: (modules, period, planKeys = {}) =>
        api.post("/billing/invoices", { modules, period, plan_keys: planKeys }),
    listInvoices: () => api.get("/billing/invoices"),
    getInvoice: (id) => api.get(`/billing/invoices/${id}`),
    cancelInvoice: (id) => api.delete(`/billing/invoices/${id}`),
    // Админские
    adminInvoices: (status) =>
        api.get("/admin/billing/invoices", { params: status ? { status } : {} }),
    adminMarkPaid: (id) => api.post(`/admin/billing/invoices/${id}/pay`),
    adminSubscriptions: () => api.get("/admin/billing/subscriptions", { timeout: 60000 }),
    adminNotifyExpiring: () => api.post("/admin/billing/notify-expiring", null, { timeout: 60000 }),
};

// ---- Юридические документы и согласия ----
export const legalApi = {
    documents: () => api.get("/legal/documents"),
    document: (slug) => api.get(`/legal/${slug}`),
    adminStatus: () => api.get("/admin/legal/status"),
    adminConsents: (limit = 200) => api.get("/admin/legal/consents", { params: { limit } }),
};

export const adminApi = {
    listUsers: () => api.get("/admin/users", { timeout: 60000 }),
    listHostels: () => api.get("/admin/hostels", { timeout: 60000 }),
    reassignHostelOwner: (id, company_id) => api.put(`/admin/hostels/${id}/owner`, { company_id }, { timeout: 120000 }),
    userDetail: (uid) => api.get(`/admin/users/${uid}/detail`, { timeout: 30000 }),
    listAiModels: () => api.get("/admin/ai-models", { timeout: 30000 }),
    setAiModels: (data) => api.put("/admin/ai-models", data, { timeout: 30000 }),
    setSubscription: (uid, data) =>
        api.put(`/admin/users/${uid}/subscription`, data, { timeout: 30000 }),
    setRole: (uid, role) =>
        api.put(`/admin/users/${uid}/role`, null, { params: { role }, timeout: 15000 }),
    setModules: (uid, data) => api.put(`/admin/users/${uid}/modules`, data, { timeout: 15000 }),
    // Режим «должник»: доступ на чтение остаётся, любая запись отбивается
    // с текстом, который админ напишет здесь.
    setDebtorBlock: (uid, enabled, message = "") =>
        api.put(`/admin/users/${uid}/debtor`, { enabled, message }, { timeout: 15000 }),
    remove: (uid) => api.delete(`/admin/users/${uid}`, { timeout: 30000 }),
    wipeDatabase: () =>
        api.post("/admin/wipe-database", null, {
            params: { confirm: "WIPE-ALL-DATA" },
            timeout: 120000,
        }),
    // Полная выгрузка: сначала пароль → одноразовый билет, потом обычная
    // навигация браузера по ссылке — архив пишется сразу на диск, а не в
    // память вкладки, как было бы через axios.
    exportSummary: () => api.get("/admin/export/summary", { timeout: 120000 }),
    // account_id — выгрузить одного клиента (его компанию) для переноса.
    startExport: (password, include_media, account_id) =>
        api.post("/admin/export/start", { password, include_media, account_id }, { timeout: 30000 }),
    exportDownloadUrl: (ticket) =>
        `${API}/admin/export/download?ticket=${encodeURIComponent(ticket)}`,
    listModuleRequests: (status) => api.get("/admin/module-requests", { params: { status } }),
    approveModuleRequest: (rid) => api.put(`/admin/module-requests/${rid}/approve`),
    rejectModuleRequest: (rid) => api.put(`/admin/module-requests/${rid}/reject`),
    searchEnterprises: (q) => api.get("/admin/enterprises-search", { params: q ? { q } : {} }),
    listEnterpriseConnections: () => api.get("/admin/enterprise-connections"),
    createEnterpriseConnection: (data) => api.post("/admin/enterprise-connections", data),
    deleteEnterpriseConnection: (id) => api.delete(`/admin/enterprise-connections/${id}`),
};

export const moduleRequestsApi = {
    list: () => api.get("/module-requests"),
    create: (module, note = "") => api.post("/module-requests", { module, note }),
    cancel: (id) => api.delete(`/module-requests/${id}`),
};

export const timesheetsApi = {
    list: (params = {}) => api.get("/timesheets", { params }),
    get: (id) => api.get(`/timesheets/${id}`),
    create: (data) => api.post("/timesheets", data),
    update: (id, data) => api.put(`/timesheets/${id}`, data),
    remove: (id) => api.delete(`/timesheets/${id}`),
    extract: (file) => {
        const fd = new FormData();
        fd.append("photo", file);
        return api.post("/timesheets/extract", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 600000,
        });
    },
    exportUrl: (period_start, period_end) =>
        `${API}/timesheets/export/xlsx?period_start=${period_start}&period_end=${period_end}`,
};

export const employeesApi = {
    list: () => api.get("/employees"),
    get: (key) => api.get(`/employees/${encodeURIComponent(key)}`),
    update: (key, data) =>
        api.put(`/employees/${encodeURIComponent(key)}`, data),
    uploadPhoto: (key, file) => {
        const fd = new FormData();
        fd.append("photo", file);
        return api.post(
            `/employees/${encodeURIComponent(key)}/photos`,
            fd,
            {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            },
        );
    },
    deletePhoto: (key, photoId) =>
        api.delete(`/employees/${encodeURIComponent(key)}/photos/${photoId}`),
    remove: (key) => api.delete(`/employees/${encodeURIComponent(key)}`),
    photoUrl: (key, photoId) =>
        `${API}/employees/${encodeURIComponent(key)}/photos/${photoId}`,
    reportUrl: (key, period_start, period_end) => {
        const params = new URLSearchParams();
        if (period_start) params.set("period_start", period_start);
        if (period_end) params.set("period_end", period_end);
        return `${API}/employees/${encodeURIComponent(key)}/report.pdf${
            params.toString() ? `?${params.toString()}` : ""
        }`;
    },
    endVahta: (key, data) =>
        api.post(`/employees/${encodeURIComponent(key)}/end-vahta`, data),
    returnVahta: (key) =>
        api.post(`/employees/${encodeURIComponent(key)}/return-vahta`),
    vahtaEndedXlsxUrl: (ps, pe) => {
        const params = new URLSearchParams();
        if (ps) params.set("ps", ps);
        if (pe) params.set("pe", pe);
        return `${API}/vahta-ended/export.xlsx${params.toString() ? `?${params.toString()}` : ""}`;
    },
    vahtaReturns: (days = 5) =>
        api.get("/vahta-returns", { params: { days } }),
    exportXlsxUrl: () => `${API}/employees/export.xlsx`,
    extractDocument: (key, docType, formData) =>
        api.post(
            `/employees/${encodeURIComponent(key)}/documents/extract?doc_type=${encodeURIComponent(docType)}`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
        ),
};

export const statsApi = {
    costs: (month) =>
        api.get("/stats/costs", { params: month ? { month } : {} }),
};

export const parseApi = {
    parseFine: (text) => api.post("/parse/fine", { text }),
    applyFine: (data) => api.post("/parse/fine/apply", data),
    parseEmployee: (text) => api.post("/parse/employee", { text }),
    applyEmployee: (data) => api.post("/parse/employee/apply", data),
};

export const periodsApi = {
    list: () => api.get("/periods"),
};

export const professionsApi = {
    list: () => api.get("/professions"),
    create: (data) => api.post("/professions", data),
    update: (id, data) => api.put(`/professions/${id}`, data),
    remove: (id) => api.delete(`/professions/${id}`),
};

export const importApi = {
    xlsx: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post("/timesheets/import/xlsx", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
        });
    },
};

export const enterprisesApi = {
    list: () => api.get("/enterprises"),
    create: (data) => api.post("/enterprises", data),
    update: (id, data) => api.put(`/enterprises/${id}`, data),
    remove: (id) => api.delete(`/enterprises/${id}`),
};


export const rentabilityApi = {
    get: (period_start, period_end) =>
        api.get("/rentability", { params: { period_start, period_end } }),
    save: (data) => api.put("/rentability/config", data),
    taxRegimes: () => api.get("/rentability/tax-regimes"),
};

export const financeOverviewApi = {
    get: (period_start, period_end, { company_id = "", owner_id = "" } = {}) =>
        api.get("/finance/overview", { params: { period_start, period_end, company_id, owner_id } }),
};

export const foodQuotasApi = {
    list: (role = "all", company_id = "") => api.get("/food-quotas", { params: { role, company_id } }),
    create: (data) => api.post("/food-quotas", data),
    approve: (id) => api.put(`/food-quotas/${id}/approve`),
    reject: (id) => api.put(`/food-quotas/${id}/reject`),
    receiptExtract: (formData) =>
        api.post("/food-quotas/receipt-extract", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    exportXlsxUrl: (company_id, period_start, period_end, role = "all") => {
        const p = new URLSearchParams({ company_id, period_start, period_end, role });
        return `${API}/food-quotas/export.xlsx?${p.toString()}`;
    },
};

export const hostelRegisterClosuresApi = {
    list: (hostel_id, period_start, period_end) =>
        api.get("/hostel/register-closures", { params: { hostel_id, period_start, period_end } }),
    save: (data) => api.post("/hostel/register-closures", data),
    remove: (id) => api.delete(`/hostel/register-closures/${id}`),
};

export const foodDailyOrdersApi = {
    list: (period_start, period_end, params = {}) =>
        api.get("/food-daily-orders", { params: { period_start, period_end, ...params } }),
    upsert: (data) => api.put("/food-daily-orders", data),
    parseImport: (text) => api.post("/food-daily-orders/parse-import", { text }),
};

export const siteDistributionApi = {
    get: (enterprise_id, date) => api.get("/site-distribution", { params: { enterprise_id, date } }),
    save: (data) => api.put("/site-distribution", data),
    parseImport: (text, enterprise_id) => api.post("/site-distribution/parse-import", { text, enterprise_id }),
};

export const ledgerTypesApi = {
    // kind: 'fine' | 'deduction' | 'bonus'
    list: (kind) => api.get(`/ledger-types/${kind}`),
    create: (kind, data) => api.post(`/ledger-types/${kind}`, data),
    update: (kind, id, data) => api.put(`/ledger-types/${kind}/${id}`, data),
    remove: (kind, id) => api.delete(`/ledger-types/${kind}/${id}`),
};

export const reportsApi = {
    aggregateUrl: (kind, period_start, period_end) => {
        const params = new URLSearchParams();
        if (period_start) params.set("period_start", period_start);
        if (period_end) params.set("period_end", period_end);
        return `${API}/reports/${kind}.pdf${params.toString() ? `?${params}` : ""}`;
    },
};

export const candidatesApi = {
    list: () => api.get("/candidates"),
    parse: (text) => api.post("/candidates/parse", { text }),
    createBulk: (candidates) => api.post("/candidates/bulk", { candidates }),
    create: (data) => api.post("/candidates", data),
    remove: (id) => api.delete(`/candidates/${id}`),
    setStage: (id, stage) => api.put(`/candidates/${id}/stage`, null, { params: { stage } }),
    setArrived: (id, arrived = true) => api.put(`/candidates/${id}/arrive`, { arrived }),
    setDocuments: (id, data) => api.put(`/candidates/${id}/documents`, data),
};

export const shiftAssignmentsApi = {
    list: (enterprise_id, date) => api.get("/shift-assignments", { params: { enterprise_id, date } }),
    create: (data) => api.post("/shift-assignments", data),
    update: (id, data) => api.put(`/shift-assignments/${id}`, data),
    remove: (id) => api.delete(`/shift-assignments/${id}`),
};

export const employeeExpensesApi = {
    list: (params = {}) => api.get("/employee-expenses", { params }),
    create: (data) => api.post("/employee-expenses", data),
    update: (id, data) => api.put(`/employee-expenses/${id}`, data),
    remove: (id) => api.delete(`/employee-expenses/${id}`),
};

// Транспорт/Питание/Одежда — a tenant with one of these modules owns a
// supply business (see backend "поставщики" section): its own price list,
// orders (both as provider and as consumer of itself or a connected
// external provider), counterparties, expenses and P&L. Each factory is
// called once per page with a fixed category ("food"|"transport"|
// "clothing") — backend keeps one collection/endpoint set per concept with
// a category field, these are frontend-only category-scoped wrappers.
export const providerPriceListApi = (category) => ({
    list: (provider_owner_id) => api.get("/provider-price-list", { params: { category, provider_owner_id } }),
    create: (data) => api.post("/provider-price-list", { ...data, category }),
    update: (id, data) => api.put(`/provider-price-list/${id}`, { ...data, category }),
    remove: (id) => api.delete(`/provider-price-list/${id}`),
});

// Clothing-only: scan one or more photos of a purchase invoice, review the
// recognized rows, then confirm to increment stock + log the purchase.
export const clothingReceiptsApi = {
    extract: (files) => {
        const fd = new FormData();
        for (const f of files) fd.append("photos", f);
        return api.post("/provider-price-list/clothing/receipt-extract", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
        });
    },
    apply: (data) => api.post("/provider-price-list/clothing/receipt", data),
    list: () => api.get("/provider-price-list/clothing/receipts"),
};

export const providerConnectionsApi = (category) => ({
    list: () => api.get("/provider-connections", { params: { category } }),
    invite: (note) => api.post("/provider-connections/invite", { category, note }),
    redeem: (invite_code) => api.post("/provider-connections/redeem", { invite_code }),
    approve: (id) => api.put(`/provider-connections/${id}/approve`),
    reject: (id) => api.put(`/provider-connections/${id}/reject`),
    revoke: (id) => api.put(`/provider-connections/${id}/revoke`),
    remove: (id) => api.delete(`/provider-connections/${id}`),
});

export const providerOrdersApi = (category) => ({
    list: (params = {}) => api.get("/provider-orders", { params: { ...params, category } }),
    create: (data) => api.post("/provider-orders", { ...data, category }),
    createBatch: (data) => api.post("/provider-orders/batch", { ...data, category }),
    remove: (id) => api.delete(`/provider-orders/${id}`),
    removeBatch: (batchId) => api.delete(`/provider-orders/batch/${batchId}`),
});

// Транспорт only — автопарк (машины/водители/маршруты).
export const vehiclesApi = {
    list: () => api.get("/transport/vehicles"),
    create: (data) => api.post("/transport/vehicles", data),
    update: (id, data) => api.put(`/transport/vehicles/${id}`, data),
    remove: (id) => api.delete(`/transport/vehicles/${id}`),
};
export const driversApi = {
    list: () => api.get("/transport/drivers"),
    create: (data) => api.post("/transport/drivers", data),
    update: (id, data) => api.put(`/transport/drivers/${id}`, data),
    remove: (id) => api.delete(`/transport/drivers/${id}`),
};
export const routesApi = {
    list: () => api.get("/transport/routes"),
    create: (data) => api.post("/transport/routes", data),
    update: (id, data) => api.put(`/transport/routes/${id}`, data),
    remove: (id) => api.delete(`/transport/routes/${id}`),
};

// ФИО / Маршрут / Кол-во поездок / Километраж — отчёт, который теперь
// обязаны сдавать менеджеры предприятий.
export const transportTripReportApi = {
    list: (period_start, period_end) => api.get("/transport/trip-report", { params: { period_start, period_end } }),
    xlsxUrl: (period_start, period_end) => {
        const p = new URLSearchParams();
        if (period_start) p.set("period_start", period_start);
        if (period_end) p.set("period_end", period_end);
        return `${API}/transport/trip-report.xlsx?${p.toString()}`;
    },
};

export const ticketsApi = {
    listBatches: (role = "all") => api.get("/transport/tickets/batches", { params: { role } }),
    createBatch: (data) => api.post("/transport/tickets/batch", data),
    listTickets: (batch_id) => api.get("/transport/tickets", { params: { batch_id } }),
    setStatus: (id, status) => api.put(`/transport/tickets/${id}/status`, { status }),
    batchPdfUrl: (batchId) => `${API}/transport/tickets/batch/${batchId}/pdf`,
    deleteSelected: (ids) => api.post("/transport/tickets/delete", { ids }),
    deleteBatch: (batchId) => api.delete(`/transport/tickets/batch/${batchId}`),
};

// Талоны на питание — тот же жизненный цикл, что у транспортных (ticketsApi
// выше): партия → печать PDF → погашение отдельных талонов.
export const foodTicketsApi = {
    listBatches: (role = "all") => api.get("/food/tickets/batches", { params: { role } }),
    createBatch: (data) => api.post("/food/tickets/batch", data),
    listTickets: (batch_id) => api.get("/food/tickets", { params: { batch_id } }),
    setStatus: (id, status) => api.put(`/food/tickets/${id}/status`, { status }),
    batchPdfUrl: (batchId) => `${API}/food/tickets/batch/${batchId}/pdf`,
    deleteSelected: (ids) => api.post("/food/tickets/delete", { ids }),
    deleteBatch: (batchId) => api.delete(`/food/tickets/batch/${batchId}`),
};

// Сканы документов жильцов в PDF — отдельно от фото в карточке жильца:
// «Контур» принимает только PDF, поэтому файл хранится как есть.
export const hostelDocumentsApi = {
    list: () => api.get("/hostel/documents"),
    upload: (formData) => api.post("/hostel/documents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
    }),
    update: (id, data) => api.put(`/hostel/documents/${id}`, data),
    remove: (id) => api.delete(`/hostel/documents/${id}`),
    fileUrl: (id) => `${API}/hostel/documents/${id}/file`,
    archiveUrl: (residentId = "") =>
        `${API}/hostel/documents/archive.zip${residentId ? `?resident_id=${residentId}` : ""}`,
};

// Досье жильцов: массовая печать и отметка «уложено в бумажную папку».
export const dossiersApi = {
    list: () => api.get("/hostel/dossiers"),
    mark: (ids, filed = true) => api.post("/hostel/dossiers/mark", { ids, filed }),
    bulkPdf: (ids) => api.post("/hostel/dossiers/bulk.pdf", { ids }, { responseType: "blob", timeout: 300000 }),
    bulkZip: (ids) => api.post("/hostel/dossiers/bulk.zip", { ids }, { responseType: "blob", timeout: 300000 }),
};

export const orgLogoApi = {
    upload: (formData) => api.post("/settings/logo", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    remove: () => api.delete("/settings/logo"),
};

// Питание (по конвенции категорийного поля, но пока используется только там)
// — регулярные заявки: "порций/халяль" каждый день без ручного повтора.
export const orderTemplatesApi = (category) => ({
    list: () => api.get("/order-templates", { params: { category } }),
    create: (data) => api.post("/order-templates", { ...data, category }),
    toggle: (id) => api.put(`/order-templates/${id}/toggle`),
    remove: (id) => api.delete(`/order-templates/${id}`),
});

export const providerCounterpartiesApi = (category) => ({
    list: () => api.get("/provider-counterparties", { params: { category } }),
    get: (id) => api.get(`/provider-counterparties/${id}`),
    create: (data) => api.post("/provider-counterparties", { ...data, category }),
    update: (id, data) => api.put(`/provider-counterparties/${id}`, { ...data, category }),
    remove: (id) => api.delete(`/provider-counterparties/${id}`),
});

export const providerCounterpartyDocsApi = {
    list: (cpid) => api.get(`/provider-counterparties/${cpid}/documents`),
    upload: (cpid, formData) =>
        api.post(`/provider-counterparties/${cpid}/documents`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
        }),
    remove: (cpid, docId) => api.delete(`/provider-counterparties/${cpid}/documents/${docId}`),
    downloadUrl: (cpid, docId) => `${API}/provider-counterparties/${cpid}/documents/${docId}/download`,
};

export const providerExpensesApi = (category) => ({
    list: (params = {}) => api.get("/provider-expenses", { params: { ...params, category } }),
    create: (data) => api.post("/provider-expenses", { ...data, category }),
    remove: (id) => api.delete(`/provider-expenses/${id}`),
});

export const providerReportApi = (category) => ({
    get: (period_start, period_end) =>
        api.get("/provider-report", { params: { category, period_start, period_end } }),
});

export const dailyReportsApi = {
    get: (enterprise_id, date) => api.get("/daily-reports", { params: { enterprise_id, date } }),
    setRequested: (enterprise_id, date, data) =>
        api.put("/daily-reports", data, { params: { enterprise_id, date } }),
    finalize: (enterprise_id, date) =>
        api.post("/daily-reports/finalize", null, { params: { enterprise_id, date } }),
    clear: (enterprise_id, date) =>
        api.post("/daily-reports/clear", null, { params: { enterprise_id, date } }),
    parseImport: (enterprise_id, text) =>
        api.post("/daily-reports/parse-import", { enterprise_id, text }, { timeout: 60000 }),
    applyImport: (data) => api.post("/daily-reports/apply-import", data),
    dates: (enterprise_id) => api.get("/daily-reports/dates", { params: { enterprise_id } }),
};

// ---- Unified Notifications ----
export const notificationsApi = {
    all: () => api.get("/notifications"),
    getSettings: () => api.get("/notification-settings"),
    updateSettings: (data) => api.put("/notification-settings", data),
    markRead: (id) => api.put(`/notifications/${encodeURIComponent(id)}/read`),
    dismiss: (id) => api.delete(`/notifications/${encodeURIComponent(id)}`),
    // Legacy hostel-only (still used by Layout badge)
    hostelList: (days = 30) => api.get(`/hostel/notifications?days=${days}`),
};

export const auditLogsApi = {
    list: (params = {}) => api.get("/audit/logs", { params }),
};

// ---- Web Push ----
export const pushApi = {
    getVapidKey: () => api.get("/push/vapid-public-key"),
    subscribe: (sub) => api.post("/push/subscribe", sub),
    unsubscribe: (endpoint) => api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`),
    test: () => api.post("/push/test"),
};

export const hostelsApi = {
    list: () => api.get("/hostels"),
    create: (data) => api.post("/hostels", data),
    update: (id, data) => api.put(`/hostels/${id}`, data),
    remove: (id) => api.delete(`/hostels/${id}`),
    residents: (id) => api.get(`/hostels/${id}/residents`),
    companies: (id) => api.get(`/hostels/${id}/companies`),
    finance: (params) => api.get("/hostel/finance", { params }),
};

export const connectionsApi = {
    list: () => api.get("/connections"),
    invite: (data) => api.post("/connections/invite", data),
    redeem: (invite_code) => api.post("/connections/redeem", { invite_code }),
    approve: (id) => api.put(`/connections/${id}/approve`),
    reject: (id) => api.put(`/connections/${id}/reject`),
    revoke: (id) => api.put(`/connections/${id}/revoke`),
    remove: (id) => api.delete(`/connections/${id}`),
};

export const hostelExpensesApi = {
    create: (data) => api.post("/hostel/expenses", data),
    update: (id, data) => api.put(`/hostel/expenses/${id}`, data),
    remove: (id) => api.delete(`/hostel/expenses/${id}`),
};

export const hostelBedQuotasApi = {
    list: (params = {}) => api.get("/hostel-bed-quotas", { params }),
    create: (data) => api.post("/hostel-bed-quotas", data),
    update: (id, data) => api.put(`/hostel-bed-quotas/${id}`, data),
    remove: (id) => api.delete(`/hostel-bed-quotas/${id}`),
};

export const hostelCompaniesApi = {
    list: () => api.get("/hostel-companies"),
    get: (id) => api.get(`/hostel-companies/${id}`),
    create: (data) => api.post("/hostel-companies", data),
    update: (id, data) => api.put(`/hostel-companies/${id}`, data),
    remove: (id) => api.delete(`/hostel-companies/${id}`),
    residents: (id, period_start, period_end) =>
        api.get(`/hostel-companies/${id}/residents`, {
            params: { period_start, period_end },
        }),
    reportUrl: (id, period_start, period_end) => {
        const p = new URLSearchParams();
        if (period_start) p.set("period_start", period_start);
        if (period_end) p.set("period_end", period_end);
        return `${API}/hostel-companies/${id}/report.xlsx${p.toString() ? `?${p}` : ""}`;
    },
    importResidentsXlsx: (id, file, dryRun = true) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("dry_run", String(dryRun));
        return api.post(`/hostel-companies/${id}/residents/import.xlsx`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
        });
    },
};

// ---- Личный кабинет жильца ----
// Отдельный токен и отдельный инстанс axios: жилец — не пользователь
// системы, и его сессия не должна пересекаться с сессией коменданта.
const residentPortalTokenKey = "resident_portal_token";

const residentApi = axios.create({ baseURL: API });
residentApi.interceptors.request.use((config) => {
    const token = localStorage.getItem(residentPortalTokenKey);
    if (token) config.headers.Authorization = "Bearer " + token;
    return config;
});
residentApi.interceptors.response.use(
    (r) => r,
    (error) => {
        if (error?.response?.status === 401) {
            localStorage.removeItem(residentPortalTokenKey);
            if (window.location.pathname !== "/resident/login") {
                window.location.href = "/resident/login";
            }
        }
        return Promise.reject(error);
    },
);

export const residentPortalApi = {
    tokenKey: residentPortalTokenKey,
    login: (phone, code) => residentApi.post("/resident-portal/login", { phone, code }),
    me: () => residentApi.get("/resident-portal/me"),
    vapidPublicKey: () => api.get("/push/vapid-public-key"),
    subscribePush: (data) => residentApi.post("/resident-portal/push/subscribe", data),
    unsubscribePush: (endpoint) => residentApi.delete("/resident-portal/push/unsubscribe", { params: { endpoint } }),
    sendLocation: (data) => residentApi.post("/resident-portal/location", data),
    revokeConsent: () => residentApi.post("/resident-portal/consent/revoke"),
    logout: () => localStorage.removeItem(residentPortalTokenKey),
};

// ---- Планировка хостела ----
// Комната — прямоугольник на сетке этажа {floor, x, y, w, h}. Форма нарочно
// не привязана к способу отрисовки: изометрия, плоский план и настоящее 3D
// читают одни и те же поля.
export const hostelMapApi = {
    map: (hostelId) => api.get(`/hostels/${hostelId}/map`),
    rooms: (hostelId) => api.get(`/hostels/${hostelId}/rooms`),
    blocks: (hostelId) => api.get(`/hostels/${hostelId}/blocks`),
    createBlock: (hostelId, data) => api.post(`/hostels/${hostelId}/blocks`, data),
    updateBlock: (hostelId, blockId, data) => api.put(`/hostels/${hostelId}/blocks/${blockId}`, data),
    deleteBlock: (hostelId, blockId) => api.delete(`/hostels/${hostelId}/blocks/${blockId}`),
    createRoom: (hostelId, data) => api.post(`/hostels/${hostelId}/rooms`, data),
    updateRoom: (hostelId, roomId, data) => api.put(`/hostels/${hostelId}/rooms/${roomId}`, data),
    deleteRoom: (hostelId, roomId) => api.delete(`/hostels/${hostelId}/rooms/${roomId}`),
    generateFloor: (hostelId, data) => api.post(`/hostels/${hostelId}/floors/generate`, data),
    extractFloorPlan: (hostelId, formData) => api.post(`/hostels/${hostelId}/floor-plan/extract`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
    }),
    clearPlan: (hostelId, floor = null) => api.delete(`/hostels/${hostelId}/floor-plan`, {
        params: floor == null ? {} : { floor },
    }),
    assign: (hostelId, residentId, roomId) =>
        api.post(`/hostels/${hostelId}/rooms/assign`, { resident_id: residentId, room_id: roomId }),
    // Привести заявленную вместимость хостела к сумме мест по плану.
    syncCapacity: (hostelId) => api.post(`/hostels/${hostelId}/capacity/sync`),
};

export const residentsApi = {
    list: (q) => api.get("/residents", { params: q ? { q } : {} }),
    get: (id) => api.get(`/residents/${id}`),
    create: (data) => api.post("/residents", data),
    update: (id, data) => api.put(`/residents/${id}`, data),
    transfer: (id, data) => api.post(`/residents/${id}/transfer`, data),
    bulkTransfer: (data) => api.post("/residents/bulk-transfer", data),
    transfers: (id) => api.get(`/residents/${id}/transfers`),
    location: (id) => api.get(`/residents/${id}/location`),
    // company_id повторяет фильтр по компании с экрана: выгружается ровно то,
    // что выбрано, а не все компании разом.
    exportXlsxUrl: (hostel_id = "", status = "all", company_id = "") => {
        const p = new URLSearchParams({ hostel_id, status, company_id });
        return `${API}/residents/export.xlsx?${p.toString()}`;
    },
    remove: (id) => api.delete(`/residents/${id}`),
    stays: (id) => api.get(`/residents/${id}/stays`),
    // Оплата проживания жильцом: сумма делится на цену суток хостела,
    // дни прибавляются к уже оплаченному сроку.
    payments: (id) => api.get(`/residents/${id}/payments`),
    addPayment: (id, data) => api.post(`/residents/${id}/payments`, data),
    // kind: "refund" — возврат при досрочном выезде, сутки вычитаются.
    refundPayment: (id, data) => api.post(`/residents/${id}/payments`, { ...data, kind: "refund" }),
    deletePayment: (id, pid) => api.delete(`/residents/${id}/payments/${pid}`),

    // ---- Согласие на обработку персональных данных ----
    // Оператор для жильца — сам хостел, а не платформа: бланк печатается с
    // реквизитами организации из настроек аккаунта.
    consentFormUrl: (id, withCode = true) =>
        `${API}/residents/${id}/consent.pdf?with_code=${withCode}`,
    // Договор печатается по выбранному шаблону владельца (см.
    // contractTemplatesApi). Без template_id берётся основной шаблон.
    moveInPackUrl: (id, templateId = "") =>
        `${API}/residents/${id}/move-in-pack.pdf${templateId ? `?template_id=${encodeURIComponent(templateId)}` : ""}`,
    contractPdfUrl: (id, templateId = "") =>
        `${API}/residents/${id}/contract.pdf${templateId ? `?template_id=${encodeURIComponent(templateId)}` : ""}`,
    // Расшифровка начислений по дням — «откуда взялась эта сумма».
    // stay_id — расшифровка прошлого заезда (у вернувшегося жильца их несколько).
    dailyCharges: (id, period_start, period_end, stay_id) =>
        api.get(`/residents/${id}/daily-charges`, {
            params: { period_start, period_end, stay_id: stay_id || undefined },
        }),
    attachSignedConsent: (id, formData) =>
        api.post(`/residents/${id}/consent/signed`, formData),
    documents: (id) => api.get(`/residents/${id}/documents`),
    patchDocument: (docId, data) => api.put(`/hostel/documents/${docId}`, data),
    revokeConsent: (id) => api.post(`/residents/${id}/consent/revoke`),
    consentSummary: () => api.get("/residents/consent/summary"),
    issuePortalCode: (id) => api.post(`/residents/${id}/portal-code`),
    extractPassport: (formData) =>
        api.post("/hostel/passport/extract", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    extractDocument: (docType, formData) =>
        api.post(`/hostel/document/extract?doc_type=${encodeURIComponent(docType)}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    bulkAttachRegistration: (formData) =>
        api.post("/hostel/registration/bulk-attach", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000,
        }),
};

export const hostelDocumentTemplatesApi = {
    upload: (kind, formData) => api.post(`/settings/hostel-template/${kind}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
    }),
};

// Шаблоны договора проживания: редактируемый текст с полями подстановки.
// В одном хостеле живут жильцы разных компаний с разными условиями, поэтому
// шаблонов несколько и при печати выбирают нужный.
export const contractTemplatesApi = {
    list: () => api.get("/hostel-contract-templates"),
    fields: () => api.get("/hostel-contract-templates/fields"),
    create: (data) => api.post("/hostel-contract-templates", data),
    update: (id, data) => api.put(`/hostel-contract-templates/${id}`, data),
    remove: (id) => api.delete(`/hostel-contract-templates/${id}`),
    // Предпросмотр НЕсохранённого текста — возвращает PDF байтами.
    preview: (id, data) =>
        api.post(`/hostel-contract-templates/${id}/preview.pdf`, data, { responseType: "blob" }),
};

export const companyDocsApi = {
    extract: (formData) =>
        api.post("/hostel-companies/document/extract", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    cardPdfUrl: (cid) => `${API}/hostel-companies/${cid}/card.pdf`,
    // Attachments (contracts, invoices, scans) — separate from the OCR reqs.
    list: (cid) => api.get(`/hostel-companies/${cid}/documents`),
    upload: (cid, formData) =>
        api.post(`/hostel-companies/${cid}/documents`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
        }),
    remove: (cid, docId) =>
        api.delete(`/hostel-companies/${cid}/documents/${docId}`),
    downloadUrl: (cid, docId) =>
        `${API}/hostel-companies/${cid}/documents/${docId}/download`,
};

// Контрагенты (Counterparties) — vendors/service providers the hostel PAYS
// (dry cleaning, bed suppliers, fire inspection, ...). Distinct from
// hostelCompaniesApi, which are client companies that rent bed-space.
export const hostelCounterpartiesApi = {
    categories: () => api.get("/hostel-counterparties/categories"),
    list: (hostelId) => api.get("/hostel-counterparties", { params: hostelId ? { hostel_id: hostelId } : {} }),
    get: (id) => api.get(`/hostel-counterparties/${id}`),
    create: (data) => api.post("/hostel-counterparties", data),
    update: (id, data) => api.put(`/hostel-counterparties/${id}`, data),
    remove: (id) => api.delete(`/hostel-counterparties/${id}`),
};

export const counterpartyDocsApi = {
    list: (cpid) => api.get(`/hostel-counterparties/${cpid}/documents`),
    upload: (cpid, formData) =>
        api.post(`/hostel-counterparties/${cpid}/documents`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
        }),
    remove: (cpid, docId) =>
        api.delete(`/hostel-counterparties/${cpid}/documents/${docId}`),
    downloadUrl: (cpid, docId) =>
        `${API}/hostel-counterparties/${cpid}/documents/${docId}/download`,
};

// Складской учёт (Warehouse) — per-hostel inventory. `quantity` on the item
// is derived from its movement log (приход/расход/корректировка), never
// edited directly — see warehouseMovementsApi.create.
export const warehouseItemsApi = {
    categories: () => api.get("/warehouse-items/categories"),
    list: (hostelId) => api.get("/warehouse-items", { params: hostelId ? { hostel_id: hostelId } : {} }),
    get: (id) => api.get(`/warehouse-items/${id}`),
    create: (data) => api.post("/warehouse-items", data),
    update: (id, data) => api.put(`/warehouse-items/${id}`, data),
    remove: (id) => api.delete(`/warehouse-items/${id}`),
};

export const warehouseMovementsApi = {
    list: (itemId) => api.get(`/warehouse-items/${itemId}/movements`),
    create: (itemId, data) => api.post(`/warehouse-items/${itemId}/movements`, data),
    // Cross-item issuance history for one recipient ("кому и что выдали") —
    // pass exactly one of resident_id/recipient_company_id.
    byRecipient: (params) => api.get("/warehouse-movements", { params }),
};

// Resident dossier PDF (all data + all photos in one file)
export const residentDossierUrl = (rid) => `${API}/residents/${rid}/dossier.pdf`;

// Extend employeesApi with refresh + typed report url
employeesApi.refreshProfessions = (key) =>
    api.post(`/employees/${encodeURIComponent(key)}/refresh-professions`);
employeesApi.reportUrlTyped = (key, period_start, period_end, types) => {
    const params = new URLSearchParams();
    if (period_start) params.set("period_start", period_start);
    if (period_end) params.set("period_end", period_end);
    if (types && types.length) params.set("types", types.join(","));
    return `${API}/employees/${encodeURIComponent(key)}/report.pdf${
        params.toString() ? `?${params.toString()}` : ""
    }`;
};

// ---- Modules / Menu ----
export const modulesApi = {
    catalog: () => api.get("/modules/catalog"),
    available: () => api.get("/modules/available"),
};

export const favoritesApi = {
    get: () => api.get("/favorites"),
    update: (favorites) => api.put("/favorites", { favorites }),
};

// ---- Companies ----
export const companiesApi = {
    list: () => api.get("/companies"),
    create: (data) => api.post("/companies", data),
    update: (id, data) => api.put(`/companies/${id}`, data),
    remove: (id) => api.delete(`/companies/${id}`),
    assignAdmin: (id, userId) => api.put(`/companies/${id}/admin`, null, { params: { user_id: userId } }),
};

// ---- Company users (employees / company admins) ----
export const companyUsersApi = {
    list: () => api.get("/company/users", { timeout: 60000 }),
    workspaceHostels: () => api.get("/company/hostels"),
    create: (data) => api.post("/company/users", data),
    update: (id, data) => api.put(`/company/users/${id}`, data),
    remove: (id) => api.delete(`/company/users/${id}`),
    setModules: (id, modules) => api.put(`/company/users/${id}/modules`, modules),
};

// Настраиваемые роли компании — доступ по вкладкам (см. MODULE_TABS/
// company_roles на бэкенде). name -> {module: [tab_path,...]}.
export const companyRolesApi = {
    list: () => api.get("/company/roles"),
    create: (data) => api.post("/company/roles", data),
    update: (id, data) => api.put(`/company/roles/${id}`, data),
    remove: (id) => api.delete(`/company/roles/${id}`),
};



// Backward compat alias used by Layout.jsx
export const hostelNotificationsApi = {
    list: (days = 30) => api.get(`/hostel/notifications?days=${days}`),
};

// ---- Магазин (Shop module) ----
export const shopShopsApi = {
    list: () => api.get("/shop/shops"),
    create: (data) => api.post("/shop/shops", data),
    update: (id, data) => api.put(`/shop/shops/${id}`, data),
    remove: (id) => api.delete(`/shop/shops/${id}`),
};

export const shopSuppliersApi = {
    list: () => api.get("/shop/suppliers"),
    create: (data) => api.post("/shop/suppliers", data),
    update: (id, data) => api.put(`/shop/suppliers/${id}`, data),
    remove: (id) => api.delete(`/shop/suppliers/${id}`),
};

export const shopCustomersApi = {
    list: () => api.get("/shop/customers"),
    create: (data) => api.post("/shop/customers", data),
    update: (id, data) => api.put(`/shop/customers/${id}`, data),
    remove: (id) => api.delete(`/shop/customers/${id}`),
};

export const shopProductsApi = {
    list: () => api.get("/shop/products"),
    get: (id) => api.get(`/shop/products/${id}`),
    create: (data) => api.post("/shop/products", data),
    update: (id, data) => api.put(`/shop/products/${id}`, data),
    remove: (id) => api.delete(`/shop/products/${id}`),
};

// Настройки магазина — тип магазина (общий/продуктовый/одежда/обувь/
// электроника), один на весь аккаунт владельца. От типа зависят доп. поля
// карточки товара и требуются ли реальные складские варианты (см.
// shopProductVariantsApi) — обязательны для clothing/shoes.
export const shopSettingsApi = {
    get: () => api.get("/shop/settings"),
    update: (data) => api.put("/shop/settings", data),
};

export const shopExpensesApi = {
    categories: () => api.get("/shop/expenses/categories"),
    list: (params) => api.get("/shop/expenses", { params }),
    create: (data) => api.post("/shop/expenses", data),
    update: (id, data) => api.put(`/shop/expenses/${id}`, data),
    remove: (id) => api.delete(`/shop/expenses/${id}`),
};

export const shopProductVariantsApi = {
    list: (productId) => api.get(`/shop/products/${productId}/variants`),
    create: (productId, data) => api.post(`/shop/products/${productId}/variants`, data),
    update: (productId, variantId, data) => api.put(`/shop/products/${productId}/variants/${variantId}`, data),
    remove: (productId, variantId) => api.delete(`/shop/products/${productId}/variants/${variantId}`),
    colors: () => api.get("/shop/product-colors"),
};

export const shopStockApi = {
    list: (shopId) => api.get("/shop/stock", { params: { shop_id: shopId } }),
    setPrice: (shopId, productId, salePrice, variantId) =>
        api.put(`/shop/shops/${shopId}/products/${productId}/price`, { sale_price: salePrice, variant_id: variantId || null }),
};

export const shopPurchasesApi = {
    list: (shopId) => api.get("/shop/purchases", { params: shopId ? { shop_id: shopId } : {} }),
    get: (id) => api.get(`/shop/purchases/${id}`),
    create: (data) => api.post("/shop/purchases", data),
};

export const shopShiftsApi = {
    list: (shopId) => api.get("/shop/shifts", { params: shopId ? { shop_id: shopId } : {} }),
    active: (shopId) => api.get("/shop/shifts/active", { params: { shop_id: shopId } }),
    open: (data) => api.post("/shop/shifts/open", data),
    close: (id, data) => api.post(`/shop/shifts/${id}/close`, data),
};

export const shopSalesApi = {
    list: (params) => api.get("/shop/sales", { params }),
    get: (id) => api.get(`/shop/sales/${id}`),
    create: (shopId, data) => api.post(`/shop/shops/${shopId}/sales`, data),
    void: (id, data) => api.post(`/shop/sales/${id}/void`, data),
};

export const shopReportsApi = {
    summary: (params) => api.get("/shop/reports/summary", { params }),
};

// ---- «Курьеры» — админская сторона (личный модуль, обычный api-инстанс) ----
export const agentsApi = {
    dashboard: () => api.get("/agents/dashboard"),
    leads: (params) => api.get("/agents/leads", { params }),
    lead: (externalId) => api.get(`/agents/leads/${externalId}`),
    updateLead: (externalId, data) => api.put(`/agents/leads/${externalId}`, data),
    moveLeadCourier: (externalId, targetCourierId) =>
        api.post(`/agents/leads/${externalId}/move-courier`, { target_courier_id: targetCourierId }),
    couriers: () => api.get("/agents/couriers"),
    courier: (id) => api.get(`/agents/couriers/${id}`),
    updateCourier: (id, data) => api.put(`/agents/couriers/${id}`, data),
    deleteCourier: (id) => api.delete(`/agents/couriers/${id}`),
    detachLead: (courierId, externalId) => api.post(`/agents/couriers/${courierId}/leads/${externalId}/detach`),
    listSubmissions: (status) => api.get("/agents/lead-submissions", { params: { status } }),
    approveSubmission: (id) => api.post(`/agents/lead-submissions/${id}/approve`),
    rejectSubmission: (id) => api.post(`/agents/lead-submissions/${id}/reject`),
    addPayout: (courierId, data) => api.post(`/agents/couriers/${courierId}/payouts`, data),
    deletePayout: (id) => api.delete(`/agents/payouts/${id}`),
    payouts: () => api.get("/agents/payouts"),
    periods: (courierId) => api.get(`/agents/couriers/${courierId}/periods`),
    addPeriod: (courierId, data) => api.post(`/agents/couriers/${courierId}/periods`, data),
    updatePeriod: (id, data) => api.put(`/agents/periods/${id}`, data),
    deletePeriod: (id) => api.delete(`/agents/periods/${id}`),
    createInvite: (courierId) => api.post(`/agents/couriers/${courierId}/invite`),
    getRegistrationLink: () => api.get("/agents/settings/registration-link"),
    createRegistrationLink: () => api.post("/agents/settings/registration-link"),
    rates: (cityQ) => api.get("/agents/rates", { params: cityQ ? { city_q: cityQ } : {} }),
    addRate: (data) => api.post("/agents/rates", data),
    deleteRate: (id) => api.delete(`/agents/rates/${id}`),
    importRates: ({ text, file } = {}) => {
        const fd = new FormData();
        if (text) fd.append("pasted_text", text);
        if (file) fd.append("file", file);
        return api.post("/agents/rates/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    importLeadsCsv: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post("/agents/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    imports: () => api.get("/agents/imports"),
    tiers: () => api.get("/agents/settings/tiers"),
    addTier: (data) => api.post("/agents/settings/tiers", data),
    updateTier: (id, data) => api.put(`/agents/settings/tiers/${id}`, data),
    deleteTier: (id) => api.delete(`/agents/settings/tiers/${id}`),
    faqList: () => api.get("/agents/settings/faq"),
    addFaq: (data) => api.post("/agents/settings/faq", data),
    updateFaq: (id, data) => api.put(`/agents/settings/faq/${id}`, data),
    deleteFaq: (id) => api.delete(`/agents/settings/faq/${id}`),
};

export const COURIER_LEAD_STATUS_LABELS = {
    created: "Создан",
    invited_to_hub: "Приглашен в Хаб",
    active: "Активный",
    active_1: "Активный1",
    active_5: "Активный5",
    active_10: "Активный10",
    active_25: "Активный25",
    rolled: "Откатан",
    not_lead: "Не лид",
};

// ---- Портал курьеров — ПОЛНОСТЬЮ отдельная авторизация от основного
// приложения: свой axios-инстанс, свой ключ в localStorage, свой редирект
// на 401. Курьер и сотрудник-табельщик могут быть залогинены в одном
// браузере одновременно без конфликта токенов.
const COURIER_PORTAL_TOKEN_KEY = "courier_portal_token";

export const courierPortalHttp = axios.create({ baseURL: API });

courierPortalHttp.interceptors.request.use((config) => {
    const token = typeof localStorage !== "undefined" ? localStorage.getItem(COURIER_PORTAL_TOKEN_KEY) : null;
    if (token) config.headers.Authorization = "Bearer " + token;
    return config;
});

courierPortalHttp.interceptors.response.use(
    (r) => r,
    (error) => {
        if (error?.response?.status === 401) {
            localStorage.removeItem(COURIER_PORTAL_TOKEN_KEY);
            if (window.location.pathname !== "/couriers/login") {
                window.location.href = "/couriers/login";
            }
        }
        return Promise.reject(error);
    },
);

export const courierPortalApi = {
    tokenKey: COURIER_PORTAL_TOKEN_KEY,
    register: (data) => courierPortalHttp.post("/courier-portal/register", data),
    login: (data) => courierPortalHttp.post("/courier-portal/login", data),
    me: () => courierPortalHttp.get("/courier-portal/me"),
    leads: () => courierPortalHttp.get("/courier-portal/leads"),
    lead: (externalId) => courierPortalHttp.get(`/courier-portal/leads/${externalId}`),
    setOwnerPayment: (externalId, amount) =>
        courierPortalHttp.put(`/courier-portal/leads/${externalId}/owner-payment`, { owner_payment_amount: amount }),
    setBagStatus: (externalId, data) =>
        courierPortalHttp.put(`/courier-portal/leads/${externalId}/bag-status`, data),
    setLastOrderDate: (externalId, lastOrderDate) =>
        courierPortalHttp.put(`/courier-portal/leads/${externalId}/last-order-date`, { last_order_date: lastOrderDate }),
    payouts: () => courierPortalHttp.get("/courier-portal/payouts"),
    rates: (cityQ) => courierPortalHttp.get("/courier-portal/rates", { params: cityQ ? { city_q: cityQ } : {} }),
    submitLead: (data) => courierPortalHttp.post("/courier-portal/lead-submissions", data),
    submissions: () => courierPortalHttp.get("/courier-portal/lead-submissions"),
    setRequisites: (data) => courierPortalHttp.put("/courier-portal/requisites", data),
    faq: () => courierPortalHttp.get("/courier-portal/faq"),
    // Поддержка: у курьера только «обычное обращение», тип на бэкенде
    // проставляется жёстко (см. courier_portal_support_create_ticket).
    supportTickets: () => courierPortalHttp.get("/courier-portal/support/tickets"),
    supportUnreadCount: () => courierPortalHttp.get("/courier-portal/support/unread-count"),
    supportTicket: (tid) => courierPortalHttp.get(`/courier-portal/support/tickets/${tid}`),
    supportCreateTicket: (formData) =>
        courierPortalHttp.post("/courier-portal/support/tickets", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    supportReply: (tid, formData) =>
        courierPortalHttp.post(`/courier-portal/support/tickets/${tid}/messages`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
};

// ---- Поддержка ----
// Тикеты (обращения) с типом и статусом, а не сплошной чат: все обращения —
// и клиентов основного приложения, и курьеров из портала — сходятся в один
// инбокс главного администратора (вкладка «Поддержка» в /admin).
export const SUPPORT_TYPES = ["general", "bug", "feature"];
export const SUPPORT_TYPE_LABELS = {
    general: "Обычное обращение",
    bug: "Нашёл баг",
    feature: "Предложение по улучшению",
};
export const SUPPORT_TYPE_COLORS = {
    general: "#3b82f6",
    bug: "#ef4444",
    feature: "#8b5cf6",
};
export const SUPPORT_STATUS_LABELS = {
    open: "Новое",
    in_progress: "В работе",
    closed: "Закрыто",
};

export const supportApi = {
    list: () => api.get("/support/tickets"),
    unreadCount: () => api.get("/support/unread-count"),
    get: (tid) => api.get(`/support/tickets/${tid}`),
    create: (formData) => api.post("/support/tickets", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    reply: (tid, formData) => api.post(`/support/tickets/${tid}/messages`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
};

export const adminSupportApi = {
    list: (params) => api.get("/admin/support/tickets", { params }),
    unreadCount: () => api.get("/admin/support/unread-count"),
    get: (tid) => api.get(`/admin/support/tickets/${tid}`),
    reply: (tid, formData) => api.post(`/admin/support/tickets/${tid}/messages`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    setStatus: (tid, status) => api.put(`/admin/support/tickets/${tid}/status`, { status }),
    remove: (tid) => api.delete(`/admin/support/tickets/${tid}`),
};


// ---- Привлечение (лидогенерация) ----
// Лид живёт только до конвертации: дальше человек становится кандидатом,
// жильцом или покупателем, а модуль лишь подтягивает исход из целевой
// сущности (см. leadgen-блок в server.py).
export const LEAD_STATUSES = ["new", "no_answer", "in_progress", "scheduled", "converted", "rejected", "spam"];
export const LEAD_STATUS_LABELS = {
    new: "Новый",
    no_answer: "Не дозвонились",
    in_progress: "В работе",
    scheduled: "Назначено",
    converted: "Конвертирован",
    rejected: "Отказ",
    spam: "Спам",
};
export const LEAD_STATUS_COLORS = {
    new: "#3b82f6",
    no_answer: "#eab308",
    in_progress: "#f97316",
    scheduled: "#8b5cf6",
    converted: "#22c55e",
    rejected: "#ef4444",
    spam: "#64748b",
};
export const LEAD_TARGETS = ["work", "housing", "purchase"];
export const LEAD_TARGET_LABELS = { work: "Работа", housing: "Жильё", purchase: "Покупка" };
export const LEAD_CHANNEL_KINDS = ["avito", "hh", "telegram", "vk", "site", "referral", "offline", "other"];
export const LEAD_CHANNEL_KIND_LABELS = {
    avito: "Авито",
    hh: "HeadHunter",
    telegram: "Telegram",
    vk: "ВКонтакте",
    site: "Сайт",
    referral: "Сарафан / реферал",
    offline: "Офлайн (расклейка)",
    other: "Другое",
};
export const LEAD_TOUCH_KINDS = ["call", "no_answer", "message", "meeting", "note"];
export const LEAD_TOUCH_LABELS = {
    call: "Звонок",
    no_answer: "Не дозвонился",
    message: "Сообщение",
    meeting: "Встреча",
    note: "Заметка",
    status: "Смена статуса",
};
export const LEAD_OUTCOME_LABELS = {
    arrived: "Прибыл",
    hired: "Трудоустроен",
    moved_in: "Заселился",
    purchased: "Купил",
};

export const leadgenApi = {
    channels: () => api.get("/leadgen/channels"),
    createChannel: (data) => api.post("/leadgen/channels", data),
    updateChannel: (cid, data) => api.put(`/leadgen/channels/${cid}`, data),
    deleteChannel: (cid) => api.delete(`/leadgen/channels/${cid}`),

    // Ссылки-вакансии: канал — это площадка и деньги, а ссылка — конкретная
    // вакансия со своей анкетой и своим переходом после отправки.
    formTemplates: () => api.get("/leadgen/form-templates"),
    forms: () => api.get("/leadgen/forms"),
    form: (fid) => api.get(`/leadgen/forms/${fid}`),
    createForm: (data) => api.post("/leadgen/forms", data),
    updateForm: (fid, data) => api.put(`/leadgen/forms/${fid}`, data),
    deleteForm: (fid) => api.delete(`/leadgen/forms/${fid}`),

    landings: () => api.get("/leadgen/landings"),
    landing: (lid) => api.get(`/leadgen/landings/${lid}`),
    createLanding: (data) => api.post("/leadgen/landings", data),
    updateLanding: (lid, data) => api.put(`/leadgen/landings/${lid}`, data),
    deleteLanding: (lid) => api.delete(`/leadgen/landings/${lid}`),
    uploadLandingImage: (lid, formData) =>
        api.post(`/leadgen/landings/${lid}/images`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    deleteLandingImage: (lid, imageId) => api.delete(`/leadgen/landings/${lid}/images/${imageId}`),

    messages: (status) => api.get("/leadgen/messages", { params: status ? { status } : {} }),
    messagesUnreadCount: () => api.get("/leadgen/messages/unread-count"),
    setMessageStatus: (mid, status) => api.put(`/leadgen/messages/${mid}/status`, { status }),
    deleteMessage: (mid) => api.delete(`/leadgen/messages/${mid}`),

    // QR требует авторизации, поэтому тянем блобом, а не через <img src> —
    // тот же приём, что нужен любому защищённому бинарнику.
    formQr: (fid, base) =>
        api.get(`/leadgen/forms/${fid}/qr.png`, { params: { base }, responseType: "blob" }),
    landingQr: (lid, base) =>
        api.get(`/leadgen/landings/${lid}/qr.png`, { params: { base }, responseType: "blob" }),

    costs: (params) => api.get("/leadgen/costs", { params }),
    createCost: (data) => api.post("/leadgen/costs", data),
    updateCost: (id, data) => api.put(`/leadgen/costs/${id}`, data),
    deleteCost: (id) => api.delete(`/leadgen/costs/${id}`),

    leads: (params) => api.get("/leadgen/leads", { params }),
    lead: (lid) => api.get(`/leadgen/leads/${lid}`),
    createLead: (data) => api.post("/leadgen/leads", data),
    updateLead: (lid, data) => api.put(`/leadgen/leads/${lid}`, data),
    deleteLead: (lid) => api.delete(`/leadgen/leads/${lid}`),
    addTouch: (lid, data) => api.post(`/leadgen/leads/${lid}/touches`, data),
    setStatus: (lid, data) => api.put(`/leadgen/leads/${lid}/status`, data),
    assign: (lid, assignedTo) => api.post(`/leadgen/leads/${lid}/assign`, { assigned_to: assignedTo }),
    convert: (lid, target) => api.post(`/leadgen/leads/${lid}/convert`, { target }),
    parseLeads: (text) => api.post("/leadgen/leads/parse", { text }, { timeout: 120000 }),
    createLeadsBulk: (data) => api.post("/leadgen/leads/bulk", data, { timeout: 60000 }),

    recruiters: () => api.get("/leadgen/recruiters"),
    settings: () => api.get("/leadgen/settings"),
    updateSettings: (data) => api.put("/leadgen/settings", data),
    stats: (params) => api.get("/leadgen/stats", { params, timeout: 60000 }),
};

// Публичная форма отклика — БЕЗ токена: её открывает соискатель, а не
// пользователь системы. Отдельный axios-инстанс, чтобы request-интерцептор
// основного не подставлял чужой Authorization и не редиректил на /login.
const publicHttp = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });
export const publicApplyApi = {
    form: (slug) => publicHttp.get(`/public/form/${slug}`),
    submit: (slug, data) => publicHttp.post(`/public/form/${slug}`, data),
    landing: (slug) => publicHttp.get(`/public/landing/${slug}`),
    landingMessage: (slug, data) => publicHttp.post(`/public/landing/${slug}/message`, data),
};

export const LEAD_FIELD_TYPES = ["text", "textarea", "phone", "number", "date", "select", "checkbox"];
export const LEAD_FIELD_TYPE_LABELS = {
    text: "Строка",
    textarea: "Многострочный текст",
    phone: "Телефон",
    number: "Число",
    date: "Дата",
    select: "Список вариантов",
    checkbox: "Галочка",
};
// Пустая привязка = поле уходит в lead.extra и не мешает поиску по телефону
// и дедупликации; см. LEAD_FIELD_MAPS на бэкенде.
export const LEAD_FIELD_MAP_LABELS = {
    "": "Отдельное поле анкеты",
    full_name: "ФИО лида",
    phone: "Телефон лида",
    contact: "Доп. контакт",
    city: "Город",
    desired_role: "Что ищет",
    comment: "Комментарий",
};
export const LEAD_EMPLOYMENT_TYPES = ["FULL_TIME", "PART_TIME", "CONTRACTOR", "TEMPORARY"];
export const LEAD_EMPLOYMENT_LABELS = {
    FULL_TIME: "Полная занятость",
    PART_TIME: "Частичная занятость",
    CONTRACTOR: "Подряд / самозанятость",
    TEMPORARY: "Временная работа / вахта",
};

export const LEAD_FORM_AFTER_LABELS = {
    thanks: "Показать «спасибо»",
    redirect: "Перевести на внешний адрес",
    landing: "Перевести на наш лендинг",
};
export const LANDING_MESSAGE_STATUS_LABELS = { new: "Новое", read: "Прочитано", done: "Обработано" };

// ---- Нейрохам ----
// Развлекательный бот. Цифр не называет принципиально: отшучивается и
// отправляет в нужный раздел — маршрут бэкенд валидирует по списку вкладок,
// доступных именно этому пользователю.
export const NEUROHAM_TONES = ["polite", "cheeky", "savage"];
export const NEUROHAM_TONE_LABELS = {
    polite: "Вежливый",
    cheeky: "Дерзкий",
    savage: "Максимальный хам",
};
export const NEUROHAM_TONE_HINTS = {
    polite: "Лёгкая ирония, без грубости",
    cheeky: "Подкалывает за лень — как приятель в курилке. Без мата",
    savage: "18+ · с матом и мемами. Включайте, если точно готовы",
};

export const neurohamApi = {
    history: () => api.get("/neuroham/messages"),
    send: (text, currentPath) =>
        api.post("/neuroham/messages", { text, current_path: currentPath }, { timeout: 120000 }),
    clear: () => api.delete("/neuroham/messages"),
};
